import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';

const ADMIN_API_URL =
  process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:8001';

export type BillingStatus =
  | 'billed'
  | 'unidentified'
  | 'no_units'
  | 'not_in_catalog'
  | 'unpriced';

export type GroupByDimension =
  | 'user'
  | 'api_key'
  | 'provider'
  | 'model'
  | 'billing_status';

export interface UsageGroup {
  user_id: number | null;
  user_email: string | null;
  user_name: string | null;
  api_key_id: number | null;
  api_key_masked: string | null;
  provider: string | null;
  model_name: string | null;
  billing_status: BillingStatus | null;
  requests_count: number;
  billed_requests_count: number;
  // Списанные баллы как decimal-строка (без потери точности).
  balls_spent: string;
  input_tokens: number;
  output_tokens: number;
  unique_users: number;
  unique_api_keys: number;
  first_seen: number | null;
  last_seen: number | null;
}

export interface UsageAggregateResponse {
  items: UsageGroup[];
  total: number;
  offset: number;
  limit: number;
  group_by: GroupByDimension[];
}

export interface UsageSummaryResponse {
  totals: UsageGroup | null;
  by_status: UsageGroup[];
}

export interface ModelOption {
  provider: string | null;
  model_name: string;
}

export interface UserOption {
  user_id: number;
  email: string | null;
  name: string | null;
}

export interface ApiKeyOption {
  api_key_id: number;
  api_key_masked: string;
  user_id: number;
}

export interface FilterOptionsResponse {
  providers: string[];
  models: ModelOption[];
  users: UserOption[];
  api_keys: ApiKeyOption[];
  billing_statuses: BillingStatus[];
  group_by_dimensions: GroupByDimension[];
}

export interface UsageAnalyticsFilters {
  periodFrom: string;
  periodTo: string;
  userIds?: number[];
  apiKeyIds?: number[];
  providers?: string[];
  models?: string[];
  billingStatuses?: BillingStatus[];
}

const buildFilterParams = (filters: UsageAnalyticsFilters): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('period_from', filters.periodFrom);
  params.set('period_to', filters.periodTo);
  (filters.userIds || []).forEach((id) => params.append('user_id', String(id)));
  (filters.apiKeyIds || []).forEach((id) =>
    params.append('api_key_id', String(id)),
  );
  (filters.providers || []).forEach((provider) =>
    params.append('provider', provider),
  );
  (filters.models || []).forEach((model) => params.append('model', model));
  (filters.billingStatuses || []).forEach((status) =>
    params.append('billing_status', status),
  );
  return params;
};

export const getUsageSummary = async (
  filters: UsageAnalyticsFilters,
): Promise<UsageSummaryResponse> => {
  const params = buildFilterParams(filters);
  const response = await axios.get<UsageSummaryResponse>(
    `${ADMIN_API_URL}/admin/usage-analytics/summary?${params.toString()}`,
    { headers: await getAuthHeaders() },
  );
  return response.data;
};

export const getUsageAggregate = async (
  filters: UsageAnalyticsFilters,
  groupBy: GroupByDimension[],
  offset: number,
  limit: number,
): Promise<UsageAggregateResponse> => {
  const params = buildFilterParams(filters);
  groupBy.forEach((dimension) => params.append('group_by', dimension));
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  const response = await axios.get<UsageAggregateResponse>(
    `${ADMIN_API_URL}/admin/usage-analytics/aggregate?${params.toString()}`,
    { headers: await getAuthHeaders() },
  );
  return response.data;
};

export const getUsageFilterOptions = async (
  periodFrom: string,
  periodTo: string,
): Promise<FilterOptionsResponse> => {
  const params = new URLSearchParams({
    period_from: periodFrom,
    period_to: periodTo,
  });
  const response = await axios.get<FilterOptionsResponse>(
    `${ADMIN_API_URL}/admin/usage-analytics/options?${params.toString()}`,
    { headers: await getAuthHeaders() },
  );
  return response.data;
};
